import { useMemo } from "react";
import { t } from "ttag";
import _ from "underscore";

import { useGetTaskQuery, useListDatabasesQuery } from "metabase/api";
import { CodeEditor } from "metabase/common/components/CodeEditor";
import { CopyButton } from "metabase/common/components/CopyButton";
import { DateTime } from "metabase/common/components/DateTime";
import { Link } from "metabase/common/components/Link";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { LogsViewer } from "metabase/monitor/components/LogsViewer";
import { MonitorHeaderTitle } from "metabase/monitor/components/MonitorHeaderTitle";
import { MonitorMain } from "metabase/monitor/components/MonitorLayout";
import { MonitorPageContent } from "metabase/monitor/components/MonitorPageContent";
import { useParams } from "metabase/router";
import {
  Anchor,
  Box,
  Button,
  Flex,
  Icon,
  Loader,
  Stack,
  Text,
  Tooltip,
} from "metabase/ui";
import * as Urls from "metabase/urls";
import { EMPTY_CELL_PLACEHOLDER } from "metabase/utils/constants";
import { openSaveDialog } from "metabase/utils/dom";
import type { Database } from "metabase-types/api";

import { formatTaskDetails, getFilename } from "../../utils";
import { MonitorBackLink } from "../MonitorBackLink";
import { TaskStatusBadge } from "../TaskStatusBadge";

import S from "./TaskDetailsPage.module.css";

export const TaskDetailsPage = () => {
  const { taskId } = useParams();
  const { data: task, error, isLoading } = useGetTaskQuery(Number(taskId));
  const code = formatTaskDetails(task);
  const linesCount = useMemo(() => code.split("\n").length, [code]);
  const { data: databasesData, isLoading: isLoadingDatabases } =
    useListDatabasesQuery();

  if (!task || error || isLoading) {
    return <LoadingAndErrorWrapper error={error} loading={isLoading} />;
  }
  const hasLogs = Boolean(task.logs?.length);
  const databases = databasesData?.data ?? [];
  const databaseByID: Record<number, Database> = _.indexBy(databases, "id");
  const db = task.db_id ? databaseByID[task.db_id] : null;
  const dbName = db?.name ?? t`Unknown name`;
  const dbEngine = db?.engine ?? t`Unknown engine`;

  const handleDownload = () => {
    const filename = getFilename(task);
    const blob = new Blob([code], { type: "text/json" });
    openSaveDialog(filename, blob);
  };

  return (
    <Flex h="100%" wrap="nowrap">
      <MonitorMain gap="lg">
        <MonitorBackLink
          to={Urls.monitorTasksList()}
          label={t`Back to Tasks`}
        />

        <MonitorPageContent className={S.content}>
          <Stack gap="sm">
            <MonitorHeaderTitle>{t`Task details`}</MonitorHeaderTitle>
            <Flex gap="md">
              <Text fw="bold" w={120}>{t`ID`}</Text>
              <Text>{task.id}</Text>
            </Flex>
            <Flex gap="md">
              <Text fw="bold" w={120}>{t`Task`}</Text>
              <Text>{task.task}</Text>
            </Flex>
            <Flex gap="md" align="center">
              <Text fw="bold" w={120}>{t`Status`}</Text>
              <TaskStatusBadge task={task} />
            </Flex>
            {task.run_id !== null && (
              <Flex gap="md" align="baseline">
                <Text fw="bold" w={120}>{t`Task run`}</Text>
                <Anchor
                  component={Link}
                  to={Urls.monitorTaskRunDetails(task.run_id)}
                >
                  {t`Go to the corresponding run`}
                </Anchor>
              </Flex>
            )}
            <Flex gap="md">
              <Text fw="bold" w={120}>{t`DB Name`}</Text>
              <Text>
                {isLoadingDatabases ? (
                  <Loader size="xs" />
                ) : db ? (
                  dbName
                ) : (
                  EMPTY_CELL_PLACEHOLDER
                )}
              </Text>
            </Flex>
            <Flex gap="md">
              <Text fw="bold" w={120}>{t`DB Engine`}</Text>
              <Text>
                {isLoadingDatabases ? (
                  <Loader size="xs" />
                ) : db ? (
                  dbEngine
                ) : (
                  EMPTY_CELL_PLACEHOLDER
                )}
              </Text>
            </Flex>
            <Flex gap="md" align="baseline">
              <Text fw="bold" w={120}>{t`Started at`}</Text>
              <Tooltip label={task.started_at}>
                <DateTime
                  value={task.started_at}
                  unit="minute"
                  data-testid="started-at"
                />
              </Tooltip>
              <CopyButton value={task.started_at} />
            </Flex>
            <Flex gap="md">
              <Text fw="bold" w={120}>{t`Ended at`}</Text>
              {task.ended_at ? (
                <Tooltip label={task.ended_at}>
                  <DateTime
                    value={task.ended_at}
                    unit="minute"
                    data-testid="ended-at"
                  />
                </Tooltip>
              ) : (
                EMPTY_CELL_PLACEHOLDER
              )}
              {task.ended_at && <CopyButton value={task.ended_at} />}
            </Flex>
            <Flex gap="md">
              <Text fw="bold" w={120}>{t`Duration (ms)`}</Text>
              <Text>{task.duration}</Text>
            </Flex>
            <Flex justify="space-between" align="flex-end">
              <MonitorHeaderTitle>{t`JSON details`}</MonitorHeaderTitle>
              <Button
                leftSection={<Icon name="download" />}
                variant="filled"
                onClick={handleDownload}
              >{t`Download`}</Button>
            </Flex>
            <Box
              className={S.codeContainer}
              p={linesCount > 1 ? 0 : "xs"}
              pos="relative"
              mt="md"
              data-testid="code-container"
            >
              <CodeEditor
                language="json"
                lineNumbers={linesCount > 1}
                readOnly
                value={code}
              />

              <Box p="sm" pos="absolute" right={0} top={0}>
                <CopyButton value={code} />
              </Box>
            </Box>

            <MonitorHeaderTitle>{t`Logs`}</MonitorHeaderTitle>
            {hasLogs ? (
              <Box className={S.codeContainer}>
                <LogsViewer
                  logs={task?.logs ?? []}
                  data-testid="task-logs"
                ></LogsViewer>
              </Box>
            ) : (
              <Text>{t`There are no captured logs`}</Text>
            )}
          </Stack>
        </MonitorPageContent>
      </MonitorMain>
    </Flex>
  );
};
